<template>
    <div class="py-20 flex justify-center items-center min-h-screen">
        <div class="w-full max-w-5xl">
            <h1 class="flex w-full justify-center text-xl md:text-3xl font-semibold text-ihh-midnight-gray pb-5">New Account</h1>
            <form @submit.prevent="createAccount" class="w-full p-10 rounded">
                <div class="grid grid-cols-2 place-items-center">
                    <div v-for="(field, key) in user" :key="key" class="w-80 text-gray-500 font-medium">
                        <div v-if="field.type === 'date'">
                            <label :for="key">{{ field.display }}</label>
                            <div class="border h-10 rounded mb-3">
                                <input v-model="field.value" :id="key" :type="field.type" class="pl-1 w-full h-full text-gray-400 font-normal" />
                            </div>
                        </div>
                        <div v-else-if="key === 'password1'">
                            <div class="flex justify-between">
                                <label :for="key">{{ field.display }}</label>
                                <span v-if="field.required" class="font-thin text-gray-400">Required</span>
                            </div>
                            <div class="border h-10 rounded mb-3">
                                <input v-model="field.value" :id="key" :type="field.type" class="pl-1 w-full h-full text-gray-400 font-normal" />
                            </div>
                            <span class="text-red-600 text-sm font-normal">{{ passwordComplexityError }}</span>
                        </div>
                        <div v-else-if="key === 'password2'">
                            <div class="flex justify-between">
                                <label :for="key">{{ field.display }}</label>
                                <span v-if="field.required" class="font-thin text-gray-400">Required</span>
                            </div>
                            <div class="border h-10 rounded mb-3">
                                <input v-model="field.value" :id="key" :type="field.type" class="pl-1 w-full h-full text-gray-400 font-normal" />
                                <p class="text-red-600 text-sm font-normal">{{ passwordMatchError }}</p>
                            </div>
                        </div>
                        <div v-else-if="field.type !== 'select'">
                            <div class="flex justify-between">
                                <label :for="key">{{ field.display }}</label>
                                <span v-if="field.required" class="font-thin text-gray-400">Required</span>
                            </div>
                            <div :class="field.type === 'checkbox' ? 'h-10 mb-3' : 'border h-10 rounded mb-3'">
                                <input v-model="field.value" :id="key" :type="field.type" :class="field.type === 'checkbox' ? '' : 'w-full h-full font-normal pl-1'" />
                                <span class="ml-2" v-if="field.type === 'checkbox'">{{ field.details }}</span>
                            </div>
                        </div>
                        <div v-else-if="field.display === 'Country'">
                            <div class="flex justify-between">
                                <label :for="key">{{ field.display }}</label>
                                <span v-if="field.required" class="font-thin text-gray-400">Required</span>
                            </div>
                            <div>
                                <select v-model="user.country.value" @change="fetchStates" :id="key" class="border h-10 rounded w-full mb-3 text-gray-400 font-normal">
                                    <option value="" disabled selected>Select Country</option>
                                    <option v-for="country in countries" :key="country.isoCode" :value="country.isoCode">{{ country.name }}</option>
                                </select>
                            </div>
                        </div>
                        <div v-else-if="field.display === 'State'">
                            <div class="flex justify-between">
                                <label :for="key">{{ field.display }}</label>
                                <span v-if="field.required" class="font-thin text-gray-400">Required</span>
                            </div>
                            <div>
                                <select v-model="user.state.value" :id="key" class="border h-10 rounded w-full mb-3 text-gray-400 font-normal">
                                    <option disabled value="">Select State</option>
                                    <option v-for="state in states" :key="state.name" :value="state.name">{{ state.name }}</option>
                                </select>
                            </div>
                        </div>
                        <div v-else-if="key === 'addressType'">
                            <label :for="key">{{ field.display }}</label>
                            <div class="flex flex-wrap">
                                <div v-for="option in field.options" :key="option" class="flex items-center mb-3 mr-4">
                                    <input type="radio" :id="option" :value="option" v-model="field.value" class="mr-1">
                                    <label :for="option" class="text-gray-400 font-normal">{{ option }}</label>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div class="flex justify-center">
                    <button @click="createAccount" type="submit" class="mt-12 ext-sm md:text-base text-ihh-midnight-gray py-3 px-8 rounded-lg bg-ihh-dark-salmon hover:bg-ihh-salmon delay-100 transition tracking-wide">Create Account</button>
                </div>
            </form>
        </div>
    </div>
</template>

<script>
import axios from 'axios';
import { Country, State } from 'country-state-city';
import Swal from 'sweetalert2';
import { user } from "../../helpers/newUser";
import Mango from '../../helpers/mango';
import { getUser} from '../../helpers/user';

// Function for setting cookies
let setCookie = function (cname, cvalue) {
    var d = new Date();
    d.setTime(d.getTime() + (365 * 24 * 60 * 60 * 1000));
    var expires = "expires=" + d.toUTCString();
    document.cookie = cname + "=" + cvalue + ";" + expires + ";path=/";
}

export default {
    data() {
        return {
            account,
            user,
            countries: [],
            states: [],
            passwordComplexityError: null,
            passwordMatchError: null,
        }
    },
    created() {
        this.fetchCountries();
    },
    methods: {
        fetchCountries() {
            this.countries = Country.getAllCountries();
        },
        fetchStates() {
            if (this.user.country.value) {
            this.states = State.getStatesOfCountry(this.user.country.value);
            }
        },
        validatePasswordComplexity() {
            const password = this.user.password1.value;
            const hasAlphabetic = /[a-zA-Z]/.test(password);
            const hasNumeric = /[0-9]/.test(password);
            if (password.length < 7 || !hasAlphabetic || !hasNumeric) {
                this.passwordComplexityError = "Password must be at least 7 characters long and contain both alphabetic and numeric characters";
            } else {
                this.passwordComplexityError = null;
            }
        },
        validatePasswordsMatch() {
            if (this.user.password1.value !== this.user.password2.value) {
                this.passwordMatchError = "Passwords do not match";
            } else {
                this.passwordMatchError = null;
            }
        },
        validateForm() {
            const requiredFields = [
                'email',
                'password1',
                'password2',
                'firstName',
                'lastName',
                'street',
                'city',
                'state',
                'zip',
                'country',
                'phone'
            ];

            for (const field of requiredFields) {
                if (!this.user[field].value) {
                    console.log("Field is required", field);
                    return false;
                }
            }

            if (this.passwordComplexityError || this.passwordMatchError) {
                console.log("Password Complexity Error", this.passwordComplexityError);
                console.log("Password Match Error", this.passwordMatchError);
                return false;
            }

            return true;
        },
        async login() {
            let member = await this.axios.post(`http://localhost:3101/controllers/account/login`, {
                email: this.email,
                password: this.password
            });

            if(member?.memberId) {
                if (member.roles?.includes('admin')) member.admin = true

                window.localStorage.setItem('user', member.memberId)
                window.localStorage.setItem('token', member.token)
                window.localStorage.setItem('email', this.user.email)

                setCookie(`Authorization`, `${member.token}`)
                this.axios.defaults.headers.common['Authorization'] = `${member.token}`

                const user = await getUser()
                this.store.user = user
                if (user?.company?.id) this.store.theme = user.company

                //this.$emit('loggedIn')

                // let restrictedPath = this.store?.login?.next || '/'
                // if (restrictedPath) this.$router.push(restrictedPath)

                //this.$emit('hide')
            } else if (member?.invalidFields) {
                Swal.fire({ title: `Invalid ${member.invalidFields.join(', ')}`, icon: 'error' })
            }
        },
        async createAccount() {
            if (!this.validateForm()) {
                Swal.fire({
                    title: 'Form Incomplete',
                    text: 'Please fill in all required fields and ensure passwords meet the complexity requirements and match.',
                    icon: 'warning',
                    confirmButtonText: 'OK'
                });
                return;
            }

            const data = {
                    email: this.user.email.value,
                    firstName: this.user.firstName.value,
                    lastName: this.user.lastName.value,
                    company: this.user.company.value,
                    phone: this.user.phone.value,
                    address1: this.user.street.value,
                    address2: this.user.apt.value,
                    city: this.user.city.value,
                    country: this.user.country.value,
                    state: this.user.state.value,
                    zip: this.user.zip.value,
                    addressType: this.user.addressType.value,
                    password: this.user.password1.value,
                    sms: this.user.sms.value,
                    dueDate: this.user.dueDate.value,
                    hcProvider: this.user.hcProvider.value,
                };

            try {
                let response = await axios.post(`http://localhost:3101/controllers/account/create`, data);

                if (response.response.status !== 200 || response.status !== 200) {
                    this.$router.push({ path: '/sign-up-error', query: { error: response } });
                }
                else if (response.status === 200) {
                    let response = await Mango.members.save(data);

                    if (!response.id) {
                        Swal.fire('Account Exists', response, 'info')
                    } else {
                        await this.login()
                    }

                    this.$router.push({ path: '/welcome', query: { email: this.user.email.value }});
                } else {
                    this.$router.push({ path: '/sign-up-error', query: { error: response.data } });
                }

            } catch (error) {
                this.$router.push({ path: '/sign-up-error', query: { error: error.response ? error.response.data : 'An unknown error occurred' } });
            }



            //this.store.member = response?.data
        }
    },
    watch: {
        'user.password1.value': function() {
            this.validatePasswordComplexity();
        },
        'user.password2.value': function() {
            this.validatePasswordsMatch();
        }
    },
    mounted() {

    }
}
</script>

<style>

</style>
